import chroma from 'chroma-js';
import React from 'react';
import styled, { css } from 'styled-components';

export const Box = styled.div<{
  variant?: 'secondary';
  padding?: boolean;
  paddingX?: boolean;
  paddingY?: boolean;
  mb?: number;
}>`
  position: relative;
  background-color: ${({ theme }) => theme.background};
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(1100, 50)},
    0 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 12px;

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background-color: ${theme.secondary.blendToBackground(5)};
      box-shadow: 0 0 0 1px ${theme.secondary.blendToBackground(150)};
    `}

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      border-radius: 8px;
      background-color: ${({ theme }) => theme.gray.blendToBackground(24)};
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);

      ${({ theme }) =>
        theme.id !== 'default' &&
        css`
          box-shadow: 0 0 0 1px ${theme.secondary.blendToBackground(100)};
        `}
    `}

    ${({ padding }) =>
    padding &&
    css`
      padding: 24px;
    `}

    ${({ paddingX }) =>
    paddingX &&
    css`
      padding-right: 24px;
      padding-left: 24px;
    `}

    ${({ paddingY }) =>
    paddingY &&
    css`
      padding-top: 24px;
      padding-bottom: 24px;
    `}

  ${({ mb }) =>
    mb !== undefined &&
    css`
      margin-bottom: ${`${mb}px`};
    `}
`;

export const EditPage = styled.div<{ fullWidth?: boolean; smallGap?: boolean }>`
  display: grid;
  grid-gap: 24px;
  width: 100%;

  ${({ fullWidth }) =>
    !fullWidth &&
    css`
      max-width: 1080px;
      margin: 0 auto;
    `}

  ${({ smallGap }) =>
    smallGap &&
    css`
      grid-gap: 16px;
    `}
`;

export const EditTitle = styled.div`
  display: grid;
  grid-gap: 12px;
  padding-right: 32px;
`;

export const EditSection = styled.div`
  display: grid;
  grid-gap: 12px;
`;

export const EditSectionTitle = styled.div<{ size?: 'default' | 'small'; twoColumns?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${({ theme }) => theme.foreground};
  line-height: 20px;
  font-size: 16px;
  font-weight: 500;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.background};
  }

  ${({ size }) =>
    size === 'small' &&
    css`
      gap: 4px;
      color: ${({ theme }) => theme.secondary.background};
      line-height: 18px;
      font-size: 14px;

      svg {
        --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
        --icon-size: 18px;
      }
    `}

  ${({ twoColumns }) =>
    twoColumns &&
    css`
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0;

      div {
        display: flex;
        gap: 8px;
        align-items: center;

        &:not(:first-child) {
          margin-left: 24px;
        }
      }
    `};
`;

export const EditSectionInfo = styled.div`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  line-height: 20px;
  font-size: 14px;
  font-weight: 500;

  small {
    font-size: 14px;
    display: inline-block;
    margin-right: 4px;
    color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  }
`;

export const EditBox = styled(Box)<{
  smallSpacing?: boolean;
  oneColumn?: boolean;
  pale?: boolean;
  bottom?: boolean;
}>`
  padding: 20px;
  display: grid;
  grid-gap: 24px;
  align-items: flex-start;

  &[data-disabled='true'] {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      cursor: not-allowed;
      z-index: 11;
      transition: background-color 0.2s;
    }

    &:hover {
      &:before {
        background-color: ${({ theme }) => chroma(theme.background).alpha(0.4).hex()};
      }
    }
  }

  ${({ oneColumn }) =>
    !oneColumn &&
    css`
      grid-template-columns: repeat(2, minmax(0, 1fr));
    `}

  ${({ pale }) =>
    pale &&
    css<{ bottom?: boolean }>`
      grid-column: 1 / 3;
      margin: 4px -20px;
      border-radius: 0;
      background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
      box-shadow:
        0 1px 0 0 ${({ theme }) => theme.secondary.blendToBackground(1200, 70)},
        0 -1px 0 0 ${({ theme }) => theme.secondary.blendToBackground(1200, 70)};

      ${({ theme }) =>
        theme.id !== 'default' &&
        css`
          background-color: ${theme.secondary.blendToBackground(0)};
          box-shadow:
            0 1px 0 0 ${theme.secondary.blendToBackground(150)},
            0 -1px 0 0 ${theme.secondary.blendToBackground(150)};
        `}

      ${({ bottom }) =>
        bottom &&
        css`
          margin-bottom: -20px;
          border-radius: 0 0 12px 12px;
        `}
    `}

  ${({ smallSpacing }) =>
    smallSpacing &&
    css`
      padding: 20px;
      grid-gap: 24px;
    `}
`;

export const EditToolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

export const Line = styled.hr`
  height: 1px;
  width: 100%;
  margin: 0;
  border: none;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

export const Inner = styled.div<{
  p?: number | string;
  px?: number | string;
  py?: number | string;
  pt?: number | string;
  pr?: number | string;
  pb?: number | string;
  pl?: number | string;
}>`
  padding: 24px;

  ${({ p }) =>
    p !== undefined &&
    css`
      padding: ${typeof p === 'number' ? `${p}px` : p};
    `}

  ${({ px }) =>
    px !== undefined &&
    css`
      padding-right: ${typeof px === 'number' ? `${px}px` : px};
      padding-left: ${typeof px === 'number' ? `${px}px` : px};
    `}

  ${({ py }) =>
    py !== undefined &&
    css`
      padding-top: ${typeof py === 'number' ? `${py}px` : py};
      padding-bottom: ${typeof py === 'number' ? `${py}px` : py};
    `}

  ${({ pt }) =>
    pt !== undefined &&
    css`
      padding-top: ${typeof pt === 'number' ? `${pt}px` : pt};
    `}

  ${({ pr }) =>
    pr !== undefined &&
    css`
      padding-right: ${typeof pr === 'number' ? `${pr}px` : pr};
    `}

  ${({ pb }) =>
    pb !== undefined &&
    css`
      padding-bottom: ${typeof pb === 'number' ? `${pb}px` : pb};
    `}

  ${({ pl }) =>
    pl !== undefined &&
    css`
      padding-left: ${typeof pl === 'number' ? `${pl}px` : pl};
    `}

  &[data-disabled='true'] {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      cursor: not-allowed;
      z-index: 11;
      transition: background-color 0.2s;
    }

    &:hover {
      &:before {
        background-color: ${({ theme }) => chroma(theme.background).alpha(0.4).hex()};
      }
    }
  }
`;

export const Grid = styled.div<{ vertical?: boolean; gap?: number }>`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  ${({ vertical }) =>
    vertical &&
    `
    grid-template-columns: 1fr;
  `}

  ${({ gap }) =>
    gap !== undefined &&
    css`
      grid-gap: ${`${gap}px`};
    `}
`;

const FlexBase = styled.div<{
  $flexDirection?: 'column' | 'row';
  $mt?: number;
  $mr?: number;
  $mb?: number;
  $ml?: number;
  $gap?: number;
  $auto?: boolean;
  $alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  $justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
}>`
  display: flex;
  flex-direction: ${({ $flexDirection }) => $flexDirection};
  gap: ${({ $gap }) => $gap}px;
  margin-top: ${({ $mt }) => ($mt ? `${$mt}px` : undefined)};
  margin-right: ${({ $mr }) => ($mr ? `${$mr}px` : undefined)};
  margin-bottom: ${({ $mb }) => ($mb ? `${$mb}px` : undefined)};
  margin-left: ${({ $ml }) => ($ml ? `${$ml}px` : undefined)};
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};

  ${({ $auto }) =>
    $auto &&
    css`
      flex: auto;
    `}
`;

export const Flex: React.FC<
  {
    flexDirection?: 'column' | 'row';
    gap?: number;
    mt?: number;
    mr?: number;
    mb?: number;
    ml?: number;
    auto?: boolean;
    alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
    justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
  } & React.ComponentPropsWithoutRef<'div'>
> = ({ flexDirection, gap, mt, mr, mb, ml, auto, alignItems, justifyContent, ...rest }) => (
  <FlexBase
    $flexDirection={flexDirection}
    $gap={gap}
    $mt={mt}
    $mr={mr}
    $mb={mb}
    $ml={ml}
    $auto={auto}
    $alignItems={alignItems}
    $justifyContent={justifyContent}
    {...rest}
  />
);

export const Card = styled.div<{ variant?: 'primary' | 'secondary' | 'dark' }>`
  background-color: ${({ theme }) => theme.background};
  border-radius: 8px;
  box-shadow:
    0 -1px 1px
      color-mix(
        in srgb,
        ${({ theme }) => (theme.id === 'dark' ? `${theme.secondary.blendToBackground(300)}` : `${theme.background}`)}
          75%,
        transparent
      )
      inset,
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.075),
    0 1px 4px 0 transparent inset,
    0 1px 2px rgba(0, 0, 0, 0.1);

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
    `}

  ${({ variant }) =>
    variant === 'dark' &&
    css`
      background-color: ${({ theme }) => theme.body};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
    `}
`;
